import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Flame, Star, ArrowLeft, LogIn, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const serviceTypeLabels = {
  heating: 'Heating Services',
  cooling: 'Air Conditioning',
  repair: 'Repair & Maintenance',
  emergency: 'Emergency Service',
  commercial: 'Commercial HVAC',
  residential: 'Residential HVAC',
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const StarRating = ({ rating, onRate, interactive = false }) => (
  <div className="flex space-x-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-5 h-5 ${
          star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
        } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
        onClick={() => interactive && onRate && onRate(star)}
      />
    ))}
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '' });
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);

  const [reviewForm, setReviewForm] = useState({ rating: 5, text: '', serviceType: '' });
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('review_auth_token');
    if (token) {
      checkAuth(token);
    }
    fetchReviews();
  }, []);

  const checkAuth = async (token) => {
    try {
      const res = await fetch('/api/auth/check', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        localStorage.removeItem('review_auth_token');
      }
    } catch {
      localStorage.removeItem('review_auth_token');
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await fetch('/api/reviews');
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } catch {
      // Reviews will show empty
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('review_auth_token', data.token);
        setUser(data.user);
        setAuthDialogOpen(false);
        setLoginForm({ email: '', password: '' });
        toast.success('Welcome back, ' + data.user.name + '!');
      } else {
        toast.error(data.error || 'Login failed');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsAuthSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsAuthSubmitting(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupForm),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('review_auth_token', data.token);
        setUser(data.user);
        setAuthDialogOpen(false);
        setSignupForm({ name: '', email: '', password: '' });
        toast.success('Account created! Welcome, ' + data.user.name + '!');
      } else {
        toast.error(data.error || 'Signup failed');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsAuthSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('review_auth_token');
    setUser(null);
    toast.success('You have been logged out.');
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setAuthDialogOpen(true);
      return;
    }
    setIsReviewSubmitting(true);
    try {
      const token = localStorage.getItem('review_auth_token');
      const res = await fetch('/api/reviews/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reviewForm),
      });
      const data = await res.json();
      if (res.ok) {
        setReviews((prev) => [data.review, ...prev]);
        setReviewForm({ rating: 5, text: '', serviceType: '' });
        toast.success('Thank you for your review!');
      } else {
        if (res.status === 401) {
          localStorage.removeItem('review_auth_token');
          setUser(null);
          setAuthDialogOpen(true);
          toast.error('Session expired. Please log in again.');
        } else {
          toast.error(data.error || 'Failed to submit review');
        }
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Customer Reviews | HVAC Contractor Reviews | Pierce County WA"
        description="Read customer reviews for Taking Over Contracting Inc, a licensed HVAC contractor in Pierce County, WA. See what Tacoma, Puyallup, and Lakewood homeowners say about our heating and air conditioning services."
        keywords="HVAC contractor reviews Pierce County, heating contractor reviews Tacoma, AC repair reviews Puyallup, HVAC service reviews, Taking Over Contracting reviews, HVAC customer testimonials"
        canonicalPath="/reviews"
      />
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Taking Over Contracting Inc</h1>
          </Link>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/tips" className="hover:text-red-500 transition-colors font-semibold">HVAC Tips</Link>
            <Link to="/reviews" className="text-red-400 font-semibold flex items-center space-x-1">
              <Star className="w-4 h-4" />
              <span>Leave a Review</span>
            </Link>
            <a href="tel:2535017893" className="flex items-center space-x-2 hover:text-red-500 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">(253) 501-7893</span>
            </a>
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-300">
                  <User className="w-4 h-4 inline mr-1" />
                  {user.name}
                </span>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button onClick={() => setAuthDialogOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <Button asChild variant="ghost" className="text-gray-600 hover:text-red-600 -ml-4 mb-6">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
          <div className="text-center">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Customer Feedback
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Customer Reviews</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers have to say about our HVAC services. Your feedback helps us improve and serve you
              better.
            </p>
          </div>
        </div>
      </section>

      {/* Write a Review Section */}
      <section className="py-12 px-6 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          {user ? (
            <Card className="shadow-xl border-2 border-gray-100">
              <CardContent className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-black">Write a Review</h3>
                  <span className="text-sm text-gray-500">
                    Posting as <span className="font-semibold text-black">{user.name}</span>
                  </span>
                </div>
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div>
                    <Label className="text-gray-700 mb-2 block">Your Rating</Label>
                    <StarRating
                      rating={reviewForm.rating}
                      onRate={(r) => setReviewForm((prev) => ({ ...prev, rating: r }))}
                      interactive
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-2 block">Service Type</Label>
                    <select
                      value={reviewForm.serviceType}
                      onChange={(e) => setReviewForm((prev) => ({ ...prev, serviceType: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    >
                      <option value="">Select the service you received</option>
                      <option value="heating">Heating Services</option>
                      <option value="cooling">Air Conditioning</option>
                      <option value="repair">Repair & Maintenance</option>
                      <option value="emergency">Emergency Service</option>
                      <option value="commercial">Commercial HVAC</option>
                      <option value="residential">Residential HVAC</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-2 block">Your Review</Label>
                    <Textarea
                      value={reviewForm.text}
                      onChange={(e) => setReviewForm((prev) => ({ ...prev, text: e.target.value }))}
                      placeholder="Tell us about your experience..."
                      rows={4}
                      required
                      maxLength={1000}
                      className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                    />
                    <p className="text-sm text-gray-400 mt-1">{reviewForm.text.length}/1000 characters</p>
                  </div>
                  <Button
                    type="submit"
                    disabled={isReviewSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold disabled:opacity-50"
                  >
                    {isReviewSubmitting ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-8">
              <h3 className="text-2xl font-bold text-black mb-4">Want to leave a review?</h3>
              <p className="text-gray-600 mb-6">
                Sign in or create an account to share your experience with our HVAC services.
              </p>
              <Button
                onClick={() => setAuthDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In to Write a Review
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Reviews Display */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-black mb-8 text-center">
            {reviews.length > 0 ? 'What Our Customers Say' : 'No Reviews Yet'}
          </h3>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading reviews...</p>
            </div>
          ) : reviews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <Card
                  key={review.id}
                  className="border-2 border-gray-100 hover:border-red-600 hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <StarRating rating={review.rating} />
                      {review.serviceType && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                          {serviceTypeLabels[review.serviceType] || review.serviceType}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 mb-4 leading-relaxed">{review.text}</p>
                    <div className="flex justify-between items-center text-sm text-gray-500 border-t border-gray-100 pt-3">
                      <span className="font-semibold text-gray-800">{review.authorName}</span>
                      <span>{formatDate(review.createdAt)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">Be the first to leave a review!</p>
              <p className="text-gray-400">Share your experience with Taking Over Contracting Inc.</p>
            </div>
          )}
        </div>
      </section>

      {/* Auth Dialog */}
      <Dialog open={authDialogOpen} onOpenChange={setAuthDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Welcome</DialogTitle>
            <DialogDescription>Sign in or create an account to leave a review.</DialogDescription>
          </DialogHeader>
          <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Create Account</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                    className="mt-1"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                    required
                    className="mt-1"
                    placeholder="Enter your password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isAuthSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  {isAuthSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    value={signupForm.name}
                    onChange={(e) => setSignupForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    className="mt-1"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                    className="mt-1"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={signupForm.password}
                    onChange={(e) => setSignupForm((prev) => ({ ...prev, password: e.target.value }))}
                    required
                    minLength={6}
                    className="mt-1"
                    placeholder="At least 6 characters"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isAuthSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  {isAuthSubmitting ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">Need HVAC Service?</h3>
          <p className="text-xl text-gray-300 mb-8">
            Contact us today for professional heating and cooling services in Puyallup, WA.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => (window.location.href = 'tel:2535017893')}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              (253) 501-7893
            </Button>
            <Button asChild className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6 rounded-lg">
              <Link to="/#contact">Request Service</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4 hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Taking Over Contracting Inc</h3>
              </Link>
              <p className="text-gray-400">
                Your trusted HVAC partner in Puyallup, WA. Professional heating, cooling, and HVAC services.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/" className="hover:text-red-500 transition-colors">Home</Link>
                </li>
                <li>
                  <Link to="/tips" className="hover:text-red-500 transition-colors">HVAC Tips</Link>
                </li>
                <li>
                  <Link to="/reviews" className="hover:text-red-500 transition-colors">Reviews</Link>
                </li>
                <li>
                  <Link to="/#contact" className="hover:text-red-500 transition-colors">Contact</Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:2535017893" className="hover:text-red-500 transition-colors">(253) 501-7893</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a
                    href="mailto:josh.takingovercontracting@gmail.com"
                    className="hover:text-red-500 transition-colors text-sm"
                  >
                    josh.takingovercontracting@gmail.com
                  </a>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span className="text-sm">
                    1723 13th Ave Ct NW
                    <br />
                    Puyallup, WA 98371
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">UBI# 60589170 &nbsp;|&nbsp; License# TAKINOC755K2</p>
            <p>&copy; {new Date().getFullYear()} Taking Over Contracting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Reviews;
